import React, { useState } from 'react';
import {
  Box, TextField, Typography, Button, AppBar, Toolbar,
  Alert, InputAdornment, IconButton, LinearProgress,
  Stepper, Step, StepLabel,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import logo from '../assets/images/logo.png';
import api from '../api/api';

// ─── helpers ────────────────────────────────────────────────────────────────

const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

const passwordStrength = (pwd) => {
  let score = 0;
  if (pwd.length >= 8)        score++;
  if (/[A-Z]/.test(pwd))      score++;
  if (/[a-z]/.test(pwd))      score++;
  if (/\d/.test(pwd))         score++;
  if (/[\W_]/.test(pwd))      score++;
  return score; // 0-5
};

const strengthLabel = (s) =>
  ['', 'Muy débil', 'Débil', 'Regular', 'Buena', 'Fuerte'][s] ?? '';
const strengthColor = (s) =>
  ['', 'error', 'error', 'warning', 'info', 'success'][s] ?? 'error';

const STEPS = ['Email', 'Verificación', 'Datos personales'];

// ─── componente ─────────────────────────────────────────────────────────────

const RegisterPage = () => {
  const navigate = useNavigate();

  const [step, setStep]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  // paso 1
  const [email, setEmail] = useState('');

  // paso 2
  const [token, setToken]       = useState('');
  const [verified, setVerified] = useState(false);

  // paso 3
  const [showPass, setShowPass]         = useState(false);
  const [showConfirm, setShowConfirm]   = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [formData, setFormData] = useState({
    first_name:    '',
    last_name:     '',
    username:      '',
    telefono:      '',
    street:        '',
    city:          '',
    province:      '',
    postal_code:   '',
    dni:           '',
    fechaNacimiento: '',
    password:      '',
    confirmPassword: '',
  });

  const set = (field) => (e) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  // ── paso 1 ────────────────────────────────────────────────────────────────
  const handleEmailSubmit = async () => {
    setError('');
    if (!email.trim()) { setError('Ingresá tu email.'); return; }
    setLoading(true);
    try {
      await api.post('/auth/send-token', { email });
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo enviar el código. Intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // ── paso 2 ────────────────────────────────────────────────────────────────
  const handleTokenVerify = async () => {
    setError('');
    if (!token.trim()) { setError('Ingresá el código de verificación.'); return; }
    setLoading(true);
    try {
      await api.post('/auth/verify-token', { email, token });
      setVerified(true);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Código inválido o expirado.');
    } finally {
      setLoading(false);
    }
  };

  // ── paso 3 ────────────────────────────────────────────────────────────────
  const handleRegister = async () => {
    setError('');

    // validaciones frontend
    const required = ['first_name', 'last_name', 'username', 'telefono', 'dni', 'fechaNacimiento', 'password', 'confirmPassword'];
    const missing  = required.filter((k) => !formData[k]?.trim());
    if (missing.length) {
      setError('Por favor completá todos los campos obligatorios (*).');
      return;
    }
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(formData.username)) {
      setError('El nombre de usuario debe tener entre 3 y 20 caracteres (letras, números y _).');
      return;
    }
    if (!passwordRegex.test(formData.password)) {
      setError('La contraseña debe tener al menos 8 caracteres, mayúsculas, minúsculas, números y símbolos.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    if (!termsAccepted) {
      setError('Debés aceptar los Términos y Condiciones y la Política de Privacidad para continuar.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/users', {
        first_name:        formData.first_name.trim(),
        last_name:         formData.last_name.trim(),
        username:          formData.username.trim(),
        email,
        password:          formData.password,
        phone:             formData.telefono,
        street:            formData.street.trim()      || undefined,
        city:              formData.city.trim()        || undefined,
        province:          formData.province.trim()    || undefined,
        postal_code:       formData.postal_code.trim() || undefined,
        dni:               formData.dni.trim(),
        birthdate:         formData.fechaNacimiento,
        role:              'user',
        // La version y la hora las pone el servidor (backend, 28-10-2026).
        acepta_terminos:   true,
      });
      navigate('/?registered=1');
    } catch (err) {
      setError(err.response?.data?.error || 'Hubo un problema con el registro. Intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // ── render ────────────────────────────────────────────────────────────────

  const strength = passwordStrength(formData.password);

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': { borderColor: '#163244' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#163244' },
  };

  const btnSx = {
    mt: 2,
    backgroundColor: '#E7A432',
    color: '#163244',
    fontWeight: 700,
    fontFamily: 'Montserrat, sans-serif',
    '&:hover': { backgroundColor: '#cf9228' },
    '&:disabled': { opacity: 0.6 },
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f0f2f5', fontFamily: 'Montserrat, sans-serif' }}>
      {/* AppBar */}
      <AppBar position="static" sx={{ backgroundColor: '#163244', boxShadow: 'none' }}>
        <Toolbar>
          <Box onClick={() => navigate('/')} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <img src={logo} alt="Fixo Logo" height={40} />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Card */}
      <Box
        sx={{
          maxWidth: 500,
          mx: 'auto',
          mt: 5,
          mb: 6,
          p: { xs: 3, sm: 4 },
          backgroundColor: '#fff',
          borderRadius: 3,
          boxShadow: '0 4px 24px rgba(22,50,68,0.10)',
        }}
      >
        <Typography
          variant="h6"
          fontWeight={700}
          fontFamily="Montserrat, sans-serif"
          color="#163244"
          mb={3}
        >
          Crear cuenta en Fixo
        </Typography>

        {/* Stepper */}
        <Stepper activeStep={step - 1} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': { fontFamily: 'Montserrat, sans-serif', fontSize: 12 },
                  '& .MuiStepIcon-root.Mui-active':    { color: '#E7A432' },
                  '& .MuiStepIcon-root.Mui-completed': { color: '#163244' },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error global */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, fontFamily: 'Montserrat, sans-serif' }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* ── PASO 1: Email ── */}
        {step === 1 && (
          <>
            <TextField
              fullWidth
              label="Email *"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleEmailSubmit()}
              margin="normal"
              sx={inputSx}
            />
            <Button
              variant="contained"
              fullWidth
              sx={btnSx}
              onClick={handleEmailSubmit}
              disabled={loading}
            >
              {loading ? 'Enviando…' : 'Enviar código de verificación'}
            </Button>
            <Typography
              variant="body2"
              textAlign="center"
              mt={2}
              sx={{ fontFamily: 'Montserrat, sans-serif', color: '#555' }}
            >
              ¿Ya tenés cuenta?{' '}
              <Box
                component="span"
                onClick={() => navigate('/')}
                sx={{ color: '#E7A432', cursor: 'pointer', fontWeight: 600 }}
              >
                Iniciá sesión
              </Box>
            </Typography>
          </>
        )}

        {/* ── PASO 2: Verificación ── */}
        {step === 2 && (
          <>
            <Alert severity="info" sx={{ mb: 2, fontFamily: 'Montserrat, sans-serif' }}>
              Revisá tu correo <strong>{email}</strong> e ingresá el código de 6 dígitos.
            </Alert>
            <TextField
              fullWidth
              label="Código de verificación *"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleTokenVerify()}
              margin="normal"
              inputProps={{ maxLength: 6 }}
              sx={inputSx}
            />
            <Button
              variant="contained"
              fullWidth
              sx={btnSx}
              onClick={handleTokenVerify}
              disabled={loading}
            >
              {loading ? 'Verificando…' : 'Verificar código'}
            </Button>
            <Button
              variant="text"
              fullWidth
              sx={{ mt: 1, color: '#163244', fontFamily: 'Montserrat, sans-serif', fontSize: 13 }}
              onClick={() => { setStep(1); setToken(''); setError(''); }}
            >
              ← Cambiar email
            </Button>
          </>
        )}

        {/* ── PASO 3: Datos personales ── */}
        {step === 3 && verified && (
          <>
            {/* — Datos personales — */}
            <Typography variant="subtitle2" fontWeight={700} color="#163244" fontFamily="Montserrat, sans-serif" mb={1}>
              Datos personales
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <TextField
                label="Nombre *"
                value={formData.first_name}
                onChange={set('first_name')}
                size="small"
                sx={inputSx}
              />
              <TextField
                label="Apellido *"
                value={formData.last_name}
                onChange={set('last_name')}
                size="small"
                sx={inputSx}
              />
            </Box>

            <TextField
              fullWidth
              label="Nombre de usuario *"
              value={formData.username}
              onChange={set('username')}
              margin="normal"
              size="small"
              helperText="3-20 caracteres. Solo letras, números y _"
              sx={inputSx}
            />

            <Box mt={1.5} mb={0.5}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontFamily: 'Montserrat, sans-serif' }}>
                Teléfono *
              </Typography>
              <PhoneInput
                country="ar"
                value={formData.telefono}
                onChange={(phone) => setFormData((p) => ({ ...p, telefono: phone }))}
                inputStyle={{
                  width: '100%',
                  height: 40,
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: 14,
                  borderColor: '#c4c4c4',
                  borderRadius: 4,
                }}
                buttonStyle={{ borderColor: '#c4c4c4', borderRadius: '4px 0 0 4px' }}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
              <TextField
                label="DNI / CUIT *"
                value={formData.dni}
                onChange={set('dni')}
                size="small"
                sx={inputSx}
              />
              <TextField
                type="date"
                label="Fecha de nacimiento *"
                InputLabelProps={{ shrink: true }}
                value={formData.fechaNacimiento}
                onChange={set('fechaNacimiento')}
                size="small"
                sx={inputSx}
              />
            </Box>

            {/* — Domicilio — */}
            <Typography variant="subtitle2" fontWeight={700} color="#163244" fontFamily="Montserrat, sans-serif" mt={3} mb={1}>
              Domicilio <Typography component="span" variant="caption" color="text.secondary">(opcional)</Typography>
            </Typography>

            <TextField
              fullWidth
              label="Calle y número"
              value={formData.street}
              onChange={set('street')}
              margin="dense"
              size="small"
              sx={inputSx}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
              <TextField
                label="Localidad"
                value={formData.city}
                onChange={set('city')}
                size="small"
                sx={inputSx}
              />
              <TextField
                label="Provincia"
                value={formData.province}
                onChange={set('province')}
                size="small"
                sx={inputSx}
              />
            </Box>
            <TextField
              label="Código postal"
              value={formData.postal_code}
              onChange={set('postal_code')}
              margin="dense"
              size="small"
              sx={{ ...inputSx, width: '50%' }}
            />

            {/* — Contraseña — */}
            <Typography variant="subtitle2" fontWeight={700} color="#163244" fontFamily="Montserrat, sans-serif" mt={3} mb={1}>
              Contraseña
            </Typography>

            <TextField
              fullWidth
              label="Contraseña *"
              type={showPass ? 'text' : 'password'}
              value={formData.password}
              onChange={set('password')}
              margin="dense"
              size="small"
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPass((v) => !v)}>
                      {showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Barra de fuerza */}
            {formData.password && (
              <Box mt={0.5} mb={1}>
                <LinearProgress
                  variant="determinate"
                  value={(strength / 5) * 100}
                  color={strengthColor(strength)}
                  sx={{ height: 4, borderRadius: 2 }}
                />
                <Typography variant="caption" color={`${strengthColor(strength)}.main`} fontFamily="Montserrat, sans-serif">
                  {strengthLabel(strength)}
                </Typography>
              </Box>
            )}

            <TextField
              fullWidth
              label="Confirmar contraseña *"
              type={showConfirm ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={set('confirmPassword')}
              margin="dense"
              size="small"
              sx={inputSx}
              error={!!formData.confirmPassword && formData.password !== formData.confirmPassword}
              helperText={
                formData.confirmPassword && formData.password !== formData.confirmPassword
                  ? 'Las contraseñas no coinciden'
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowConfirm((v) => !v)}>
                      {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* — Términos y Condiciones — */}
            <Box sx={{
              display: 'flex', alignItems: 'flex-start', gap: 1.5,
              mt: 3, mb: 1,
              p: 1.5,
              borderRadius: 2,
              border: termsAccepted
                ? '1px solid rgba(22,50,68,0.2)'
                : '1px solid rgba(231,164,50,0.4)',
              background: termsAccepted ? 'rgba(22,50,68,0.03)' : 'rgba(231,164,50,0.04)',
              transition: 'all 0.2s',
              cursor: 'pointer',
            }}
              onClick={() => setTermsAccepted(v => !v)}
            >
              {/* Checkbox custom */}
              <Box sx={{
                width: 18, height: 18, borderRadius: 0.5, flexShrink: 0, mt: 0.2,
                border: `2px solid ${termsAccepted ? '#163244' : '#E7A432'}`,
                background: termsAccepted ? '#163244' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
              }}>
                {termsAccepted && (
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, color: '#2c3e50', lineHeight: 1.6, userSelect: 'none' }}
                onClick={e => e.stopPropagation()}
              >
                He leído y acepto los{' '}
                <Link
                  to="/terminos"
                  target="_blank"
                  style={{ color: '#163244', fontWeight: 700, textDecoration: 'underline' }}
                >
                  Términos y Condiciones
                </Link>
                {' '}y la{' '}
                <Link
                  to="/privacidad"
                  target="_blank"
                  style={{ color: '#163244', fontWeight: 700, textDecoration: 'underline' }}
                >
                  Política de Privacidad
                </Link>
                {' '}de FIXO. *
              </Typography>
            </Box>

            <Button
              variant="contained"
              fullWidth
              sx={{ ...btnSx, mt: 2, opacity: termsAccepted ? 1 : 0.55 }}
              onClick={handleRegister}
              disabled={loading}
            >
              {loading ? 'Creando cuenta…' : 'Crear cuenta'}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default RegisterPage;
